import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { Loader2, Mail, Lock, UserIcon, Eye, EyeOff, Store, ArrowLeft, AlertTriangle, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAuthErrorMessage, normalizeEmail } from '@/lib/authErrorMessage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detect in-app browsers (Instagram, Facebook, etc.)
const isInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return /FBAN|FBAV|Instagram|Line\/|Snapchat|Twitter/i.test(ua);
};

export default function ClientAuthPage() {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showInAppWarning, setShowInAppWarning] = useState(false);
  const [activeFeatureTip, setActiveFeatureTip] = useState(null);
  const navigate = useNavigate();
  const { login, register } = useAuth();
  
  useEffect(() => {
    setShowInAppWarning(isInAppBrowser());
  }, []);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');
    
    try {
      if (mode === 'login') {
        const userData = await login(normalizeEmail(formData.email), formData.password);
        if (userData.is_admin) {
          navigate('/admin');
        } else {
          // Go directly to Wedding Planner for clients
          if (userData.force_password_change) {
            toast.info('Your password was reset by admin. Please set a new password now.');
            navigate('/planner', { state: { forcePasswordChange: true } });
          } else {
            navigate('/planner');
          }
        }
      } else {
        await register(formData.name.trim(), normalizeEmail(formData.email), formData.password);
        // New registrations go to Wedding Planner too
        navigate('/planner');
      }
    } catch (err) {
      console.error('Registration/Login error:', err);
      setError(getAuthErrorMessage(err, 'Registration failed. Please check your details and try again.'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    
    setForgotLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      if (response.data?.delivery_method === 'manual') {
        toast.info('Password reset email delivery is currently unavailable. Please contact the site admin for help resetting your password.');
      } else {
        toast.success('If an account exists with this email, you will receive a password reset link.');
      }
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };
  
  // Forgot Password Modal
  if (showForgotPassword) {
    return (
      <div className="min-h-screen py-12 flex items-center justify-center" data-testid="forgot-password-page">
        <div className="container-wedding max-w-md">
          <div className="text-center mb-6">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/ndr9m4c4_logo%20blk%2026%20cropped.png"
                alt="Gibraltar Wedding Expo"
                className="h-16 mx-auto"
              />
            </Link>
          </div>
          
          <div className="bg-white rounded-sm border border-stone-200 p-8">
            <button
              onClick={() => setShowForgotPassword(false)}
              className="flex items-center text-stone-500 hover:text-stone-700 mb-4"
              data-testid="client-forgot-password-back-button"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to login
            </button>
            
            <h2 className="heading-md mb-2">Forgot Password</h2>
            <p className="text-stone-600 text-sm mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="your@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="client-forgot-password-email-input"
                  />
                </div>
              </div>

              <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="client-forgot-password-note">
                Reset emails depend on the site email setup. If no email arrives, please contact the site admin.
              </div>
              
              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={forgotLoading}
                data-testid="client-forgot-password-submit"
              >
                {forgotLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Send Reset Link
              </Button>
            </form>
            
            <p className="text-center text-sm text-stone-500 mt-6">
              Remember your password?{' '}
              <button
                onClick={() => setShowForgotPassword(false)}
                className="text-[#5C0120] hover:underline"
                data-testid="client-forgot-password-signin-link"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen py-12 flex items-center justify-center" data-testid="client-auth-page">
      {/* In-App Browser Warning */}
      {showInAppWarning && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white px-4 py-3 z-50">
          <div className="max-w-md mx-auto flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-sm">You're using Instagram/Facebook browser</p>
              <p className="text-xs opacity-90">For the best experience, open in Safari (iPhone) or Chrome (Android).</p>
              <button 
                onClick={() => window.open(window.location.href, '_blank')}
                className="mt-2 inline-flex items-center gap-1 bg-white text-amber-600 px-3 py-1 rounded text-xs font-medium"
              >
                <ExternalLink className="h-3 w-3" />
                Open in Browser
              </button>
            </div>
            <button onClick={() => setShowInAppWarning(false)} className="text-white/80 hover:text-white text-lg">×</button>
          </div>
        </div>
      )}
      
      <div className={`container-wedding max-w-md ${showInAppWarning ? 'pt-20' : ''}`}>
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/ndr9m4c4_logo%20blk%2026%20cropped.png"
              alt="Gibraltar Wedding Expo"
              className="h-16 mx-auto"
            />
          </Link>
        </div>
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#5C0120]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserIcon className="h-8 w-8 text-[#5C0120]" />
          </div>
          <h1 className="heading-lg mb-2" data-testid="client-auth-welcome-title">Welcome to your Couples Portal</h1>
          <p className="text-stone-600">Plan your perfect Gibraltar wedding</p>
        </div>

        <div className="mb-6 rounded-2xl border border-[#5C0120]/15 bg-white/85 p-5" data-testid="client-auth-account-note">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#5C0120] mb-2">This page is for couples only</p>
          <p className="text-sm text-stone-600 mb-4">If you are a wedding business, please do not register here — use the vendor portal so your listing and dashboard are set up correctly.</p>
          <Link to="/vendor/auth" data-testid="client-auth-vendor-portal-link">
            <Button className="w-full bg-[#D4AF37] text-white hover:bg-[#D4AF37]/90 border border-[#D4AF37]">
              I&apos;m a vendor — take me to the vendor portal
            </Button>
          </Link>
        </div>
        
        {/* Auth Card */}
        <div className="bg-white p-8 rounded-sm border border-stone-200 shadow-sm">
          {/* Mode Toggle */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-3 text-sm font-medium rounded-sm transition-colors ${
                mode === 'login' ? 'bg-[#5C0120] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
              data-testid="client-login-tab"
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-3 text-sm font-medium rounded-sm transition-colors ${
                mode === 'register' ? 'bg-[#5C0120] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
              data-testid="client-register-tab"
            >
              Register
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <Label htmlFor="client-name">Full Name</Label>
                <div className="relative mt-1">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <Input
                    id="client-name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="client-name-input"
                  />
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="client-email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  id="client-email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  data-testid="client-email-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="client-password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  id="client-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  data-testid="client-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-stone-500 shadow-sm hover:text-stone-700"
                  data-testid="client-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {mode === 'login' && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm font-medium text-[#5C0120] hover:underline"
                  data-testid="client-forgot-password-trigger"
                >
                  Forgot password?
                </button>
              </div>
            )}
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
            
            <Button
              type="submit"
              className="w-full btn-primary"
              disabled={loading}
              data-testid="client-submit-btn"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {mode === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
        </div>

        <CouplesDashboardFeatureExplainer
          activeFeatureTip={activeFeatureTip}
          setActiveFeatureTip={setActiveFeatureTip}
        />

        {/* Vendor CTA */}
        <div className="mt-8 p-6 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-sm text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Store className="h-5 w-5 text-[#D4AF37]" />
            <span className="font-semibold text-stone-800">Are you a wedding vendor?</span>
          </div>
          <p className="text-sm text-stone-600 mb-4">
            Register your business and reach couples planning their Gibraltar wedding.
          </p>
          <Link to="/vendor/auth">
            <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid="client-auth-vendor-cta-button">
              Go to Vendor Portal
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function CouplesDashboardFeatureExplainer({ activeFeatureTip, setActiveFeatureTip }) {
  const features = [
    { key: 'chat', label: 'AI Assistant', emoji: '🤖', text: 'Get planning guidance and quick answers anytime.' },
    { key: 'messages', label: 'Messages', emoji: '💬', text: 'Keep vendor and guest conversations in one place.' },
    { key: 'notes', label: 'Notes', emoji: '📝', text: 'Capture ideas and reminders while planning.' },
    { key: 'dates', label: 'Important Dates', emoji: '📅', text: 'Track milestones and wedding deadlines.' },
    { key: 'budget', label: 'Budget Planner', emoji: '💰', text: 'Plan spending and monitor paid items.' },
    { key: 'timeline', label: 'Timeline', emoji: '⏳', text: 'Organise your wedding tasks step by step.' },
    { key: 'guests', label: 'Guest List', emoji: '👥', text: 'Manage your RSVPs and guest details.' },
    { key: 'website', label: 'Website Builder', emoji: '💻', text: 'FREE wedding website to share with your family and guests. Add photos, your love story, wedding day schedule, and collect RSVPs including dietary requirements.' },
    { key: 'gallery', label: 'Guest Gallery', emoji: '📸', text: 'FREE QR code to share on your wedding day. Let your family and guests upload their memories. After your wedding, download up to 200 images in one zip file.' },
    { key: 'favorites', label: 'Compare Vendors', emoji: '❤️', text: 'Compare shortlisted vendors before booking.' },
  ];

  const selectedFeature = features.find((feature) => feature.key === activeFeatureTip);

  return (
    <section className="mt-8 rounded-sm border border-stone-200 bg-white p-5" data-testid="client-dashboard-feature-explainer">
      <div className="mb-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5C0120]" data-testid="client-dashboard-feature-explainer-kicker">Explore Key Features</p>
        <p className="text-sm text-stone-600" data-testid="client-dashboard-feature-explainer-copy">Based on your real Couples Dashboard tools. Tap any feature to learn more.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2" data-testid="client-dashboard-feature-explainer-grid">
        {features.map((feature) => {
          const isActive = feature.key === activeFeatureTip;
          return (
            <button
              key={feature.key}
              type="button"
              onClick={() => setActiveFeatureTip((prev) => (prev === feature.key ? null : feature.key))}
              className={`rounded-sm border p-2 text-left transition-colors ${isActive ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 hover:border-[#D4AF37]'}`}
              data-testid={`client-dashboard-feature-hotspot-${feature.key}`}
            >
              <p className="text-xs font-semibold text-stone-800 leading-tight">{feature.label}</p>
              <p className="text-[11px] text-stone-500 mt-1">{feature.emoji}</p>
            </button>
          );
        })}
      </div>

      <div className="min-h-[62px] mt-3" data-testid="client-dashboard-feature-tooltip-wrap">
        {selectedFeature && (
          <div className="rounded-sm border border-stone-200 bg-stone-50 px-3 py-2" data-testid="client-dashboard-feature-tooltip">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-stone-900" data-testid="client-dashboard-feature-tooltip-title">{selectedFeature.label}</p>
                <p className="text-xs text-stone-600 mt-1" data-testid="client-dashboard-feature-tooltip-text">{selectedFeature.text}</p>
              </div>
              <button
                type="button"
                onClick={() => setActiveFeatureTip(null)}
                className="text-xs text-stone-500 hover:text-stone-700"
                data-testid="client-dashboard-feature-tooltip-close"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
